import { isReviewable, mediaLink } from './deliverables'
import { toDrivePreviewUrl } from './drive'
import { dateRange, dayMonth, shootRange, stepWhen } from './clientDates'
import { phaseLabel, type ProjectStep } from './projectSteps'
import type { DateMap } from './deliverableDates'
import type { Deliverable, Shoot } from '../types/portal'

/**
 * THE LINE ON THE DOOR.
 *
 * The client page is in two halves — **things they act on** and **things they
 * consult** — and each half ends in a full-width door to the other. The door
 * carries a live summary of what is behind it, and that line is the whole
 * idea: *The plan · 1 of 25 done · 10 waiting on you · next up: main
 * interview shoot, 26–30 October* does its job for a client who never clicks
 * it. A door labelled only "The plan" would be furniture.
 *
 * Everything here is derived at render. Nothing is stored, so nothing can go
 * stale, and a client who opens the link three weeks later reads the state of
 * the job that minute.
 */

/** Is there anything on this project a client could actually open? */
export function hasOpenableWork(items: Deliverable[]): boolean {
  return items.some(
    (item) => item.type === 'Photography' || Boolean(toDrivePreviewUrl(mediaLink(item))),
  )
}

/**
 * What is behind the door to the plan.
 *
 * How much is done, how much is sitting with them, and what happens next.
 * "Waiting on you" is the client's own steps, said in the second person,
 * because the point of putting it on a door is that it is read by someone who
 * was not going to click.
 */
export function planSummary(steps: ProjectStep[], shoots: Shoot[], today: Date = new Date()): string {
  const parts: string[] = []

  if (steps.length > 0) {
    const done = steps.filter((step) => step.status === 'done').length
    parts.push(`${done} of ${steps.length} done`)
    const waiting = steps.filter((step) => step.status === 'awaiting_client').length
    if (waiting > 0) parts.push(`${waiting} waiting on you`)
  }

  const next = nextThing(steps, shoots, today)
  if (next) parts.push(`next up: ${next}`)

  return parts.join(' · ')
}

/**
 * The next dated thing, shoot or step, whichever comes first. A shoot wins a
 * tie because it is the one a client might need to be somewhere for.
 */
function nextThing(steps: ProjectStep[], shoots: Shoot[], today: Date): string {
  const shoot = shoots[0]
  const step = steps
    .filter((item) => item.start && item.status !== 'done')
    .filter((item) => stepWhen(item.start, item.end, today) !== 'past')
    .sort((a, b) => a.start.localeCompare(b.start))[0]

  if (shoot && (!step || shoot.date <= step.start)) {
    return `${shoot.title || 'shoot'}, ${shootRange(shoot)}`
  }
  if (step) return `${step.title.toLowerCase()}, ${dateRange(step.start, step.end)}`
  return ''
}

/**
 * What is behind the door to the work.
 *
 * Anything waiting on them comes first, because that is the only line here
 * that asks for something. Otherwise the newest thing, by the date it reached
 * them.
 */
export function workSummary(items: Deliverable[], dates: DateMap): string {
  const parts: string[] = []

  const waiting = items.filter((item) => isReviewable(item.status)).length
  if (waiting > 0) parts.push(`${waiting} waiting on you`)

  let newest = { name: '', added: '' }
  for (const item of items) {
    const added = dates.get(item.id)?.added ?? ''
    if (added && added > newest.added) newest = { name: item.name, added }
  }
  if (newest.added) parts.push(`${newest.name} added ${dayMonth(newest.added)}`)
  else if (items.length > 0) parts.push(items.length === 1 ? '1 to watch' : `${items.length} to watch`)

  return parts.join(' · ')
}

/** Which stage the job is in, for the door's quiet second line. */
export function stageNow(steps: ProjectStep[], today: Date = new Date()): string {
  const live = steps.find((step) => step.start && stepWhen(step.start, step.end, today) === 'now')
  if (live) return phaseLabel(live.phase)
  const next = steps
    .filter((step) => step.start && stepWhen(step.start, step.end, today) === 'future')
    .sort((a, b) => a.start.localeCompare(b.start))[0]
  return next ? phaseLabel(next.phase) : ''
}
